import { AngularFirestore } from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

/**
 * Required import to use firebase.firestore.FieldValue
 * https://stackoverflow.com/a/52220012/4982169
 */
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { AuthUserService } from './auth/auth-user.service';

export abstract class NfmbDataService {

  protected collectionPath: string;
  protected permissions;

  constructor(
    protected afs: AngularFirestore,
    protected authUserService: AuthUserService,
    protected basePath: string
  ) {
    this.collectionPath = basePath;
  }

/**
 * Inspired by https://angularfirebase.com/lessons/firestore-advanced-usage-angularfire/#3-CRUD-Operations-with-Server-Timestamps
 */
  get timestamp(): firebase.firestore.FieldValue {
    return firebase.firestore.FieldValue.serverTimestamp();
  }

  abstract get data$();

  public getById(id: string): Observable<any> {
    return this.afs.doc<any>(`${this.collectionPath}/${id}`).valueChanges().pipe(
      switchMap( doc => {
        return of({ id, ...doc });
      })
    );
  }

  public remove(id: string): Promise<void> {
    return this.afs.doc<any>(`${this.collectionPath}/${id}`).delete();
  }

  /**
   * AngularFirestore provides methods for setting, updating
   * - set(data: T) - Destructively updates a document's data.
   * - update(data: T) - Non-destructively updates a document's data.
   */
  public set(id: string, data: any): Promise<void> {
    data.modifiedAt = this.timestamp;

    return this.afs.doc<any>(`${this.collectionPath}/${id}`).set(data);
  }

  public update(id: string, data: any): Promise<void> {
    data.modifiedAt = this.timestamp;

    return this.afs.doc<any>(`${this.collectionPath}/${id}`).update(data);
  }

  /// Helper to determine if any matching roles exist
  protected matchingRoles(allowedRoles, userRoles): boolean {
    const intersection = allowedRoles.filter(x => userRoles.includes(x));
    const result = !!intersection.length;

    return result;
  }

  public currentProfileHasPermissionsTo$(action: string): Observable<boolean> {

    return this.authUserService.currentProfile$.pipe(
      switchMap( profile => {
        const roles = (profile && profile.roles) ? profile.roles : [];

        return of(this.haveRolesPermissionsTo(roles, action));
      })
    );
  }

  protected haveRolesPermissionsTo(roles, action): boolean {

    const allowed = this.permissions.hasOwnProperty(action) ? this.permissions[action] : [];
    const hasPermissions = this.matchingRoles(allowed, Object.keys(roles));

    return hasPermissions;
  }
}
